import React, { useState, useEffect, useRef } from 'react';
import Building from 'lucide-react/dist/esm/icons/building';
import ImageIcon from 'lucide-react/dist/esm/icons/image';
import X from 'lucide-react/dist/esm/icons/x';
import Upload from 'lucide-react/dist/esm/icons/upload';
import { useAmlStore } from '../../stores/amlStore';
import { Button, Modal, TextField, Alert, Text } from '../../../../components/ui';
import { useToast } from '../../../../components/feedback';
import type { AmlClient } from '../../types/aml';
import { amlApi } from '../../services/amlApi';
import { getTranslation } from '../../../../i18n';
import { useUIStore } from '../../../../stores/uiStore';

interface ClientFormProps {
  client: AmlClient | null;
  onClose: () => void;
}

const ClientForm: React.FC<ClientFormProps> = ({ client, onClose }) => {
  const { uploadClientLogo, loading } = useAmlStore();
  const { success, error: toastError } = useToast();
  const { language } = useUIStore();
  const tr = (key: string) => getTranslation(language, key);
  const nameRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Image state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [removeExistingLogo, setRemoveExistingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isEdit = !!client;

  const existingLogoUrl = client?.image_name && !removeExistingLogo
    ? `/client-logos/${client.image_name}`
    : null;

  useEffect(() => {
    if (client) {
      setName(client.name);
      setAddress(client.address ?? '');
      setPhone(client.phone ?? '');
    } else {
      setName('');
      setAddress('');
      setPhone('');
    }
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveExistingLogo(false);
    setError(null);
    setTimeout(() => nameRef.current?.focus(), 100);
  }, [client]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const url = URL.createObjectURL(file);
    setLogoPreview(url);
  };

  const handleRemoveFile = () => {
    setLogoFile(null);
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveExistingLogo = () => {
    setRemoveExistingLogo(true);
    handleRemoveFile();
  };

  const handleSubmit = async () => {
    if (isEdit) {
      try {
        await amlApi.updateClient(client!.id, {
          name,
          address: address || null,
          phone: phone || null,
          ...(removeExistingLogo && !logoFile ? { image_name: null } : {}),
        });
      } catch (err: any) {
        setError(err?.response?.data?.detail || tr('amlClientToastFailedToUpdate'));
        return;
      }

      if (logoFile) {
        try {
          await uploadClientLogo(client!.id, logoFile);
        } catch (err: any) {
          toastError(err?.response?.data?.detail || tr('amlClientToastLogoUploadFailed'));
          await useAmlStore.getState().fetchClients();
          onClose();
          return;
        }
      }

      await useAmlStore.getState().fetchClients();
      success(tr('amlClientToastUpdated'));
      onClose();
    } else {
      let newClient: AmlClient;
      try {
        newClient = await amlApi.createClient({
          name,
          address: address || undefined,
          phone: phone || undefined,
        });
      } catch (err: any) {
        setError(err?.response?.data?.detail || tr('amlClientToastFailedToCreate'));
        return;
      }

      if (logoFile) {
        try {
          await uploadClientLogo(newClient.id, logoFile);
        } catch (err: any) {
          toastError(err?.response?.data?.detail || tr('amlClientToastCreatedLogoFailed'));
        }
      }

      await useAmlStore.getState().fetchClients();
      success(tr('amlClientToastCreated'));
      onClose();
    }
  };

  const previewSrc = logoPreview ?? existingLogoUrl;

  return (
    <Modal isOpen={true} onClose={onClose}>
      <Modal.Header title={isEdit ? tr('amlClientFormTitleEdit') : tr('amlClientFormTitleCreate')} icon={<Building size={16} />} onClose={onClose} />

      <Modal.Body className="space-y-4">
        {/* Logo upload */}
        <div className="space-y-2">
          <Text variant="label">{tr('amlClientFieldClientLogo')}</Text>
          <div className="flex items-start gap-4">
            {/* Preview */}
            <div className="w-24 h-14 rounded-lg border border-subtle bg-surface-secondary flex items-center justify-center overflow-hidden shrink-0">
              {previewSrc ? (
                <img src={previewSrc} alt="logo preview" className="w-full h-full object-contain" />
              ) : (
                <ImageIcon size={20} className="text-content-muted" />
              )}
            </div>

            {/* Controls */}
            <div className="flex flex-col gap-2">
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Upload size={14} />}
                onClick={() => fileInputRef.current?.click()}
              >
                {previewSrc ? tr('amlClientBtnChangeImage') : tr('amlClientBtnUploadImage')}
              </Button>
              {logoFile && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<X size={14} />}
                  onClick={handleRemoveFile}
                >
                  {tr('amlClientBtnCancelUpload')}
                </Button>
              )}
              {!logoFile && client?.image_name && !removeExistingLogo && (
                <Button
                  variant="danger"
                  size="sm"
                  leftIcon={<X size={14} />}
                  onClick={handleRemoveExistingLogo}
                >
                  {tr('amlClientBtnRemoveImage')}
                </Button>
              )}
              <span className="text-xs text-content-muted">{tr('amlClientImageFormats')}</span>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        <TextField
          ref={nameRef}
          label={tr('amlClientFieldClientName')}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          placeholder={tr('amlClientPlaceholderCompanyName')}
        />

        <TextField
          label={tr('amlClientFieldAddress')}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder={tr('amlClientPlaceholderBusinessAddress')}
        />

        <TextField
          label={tr('amlClientFieldPhone')}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder={tr('amlClientPlaceholderPhoneNumber')}
        />

        {error && <Alert variant="error">{error}</Alert>}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="ghost" size="lg" onClick={onClose}>
          {tr('amlBtnCancel')}
        </Button>
        <Button
          variant="primary"
          size="lg"
          loading={loading}
          disabled={!name.trim()}
          onClick={handleSubmit}
        >
          {isEdit ? tr('amlClientBtnSaveChanges') : tr('amlClientBtnCreateClient')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default React.memo(ClientForm);
